import React, { useState, useRef } from 'react';

interface InputProps {
  label: string;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  type = 'text',
  value,
  onChange,
  error = false,
  errorMessage,
  placeholder,
  disabled = false,
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleContainerClick = () => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  };

  const hasValue = value.length > 0;
  const showFloatingLabel = hasValue || isFocused;

  // Determine border color
  let borderColor = "border-zinc-300";
  if (error) {
    borderColor = "border-red-400";
  } else if (isFocused) {
    borderColor = "border-zinc-500";
  } else if (hasValue) {
    borderColor = "border-zinc-400";
  }

  return (
    <div className={`w-full ${className}`}>
      <div 
        className="relative rounded-lg h-[60px] cursor-text"
        onClick={handleContainerClick}
      >
        <div className={`absolute border ${borderColor} border-solid inset-0 pointer-events-none rounded-lg`} />
        
        {/* Floating label */}
        {showFloatingLabel && (
          <div className="absolute top-2 left-4 text-zinc-500 text-sm  pointer-events-none">
            {label}
          </div>
        )}
        
        <div className="flex flex-row items-center relative h-full">
          <div className={`box-border flex flex-row items-center justify-start px-4 relative w-full h-full ${showFloatingLabel ? 'pt-6 pb-2' : 'py-4'}`}>
            <input
              ref={inputRef}
              type={type}
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={showFloatingLabel ? '' : (placeholder || label)}
              disabled={disabled}
              className={` font-normal text-base bg-transparent border-none outline-none w-full flex-grow ${
                showFloatingLabel ? 'text-zinc-700' : 'text-zinc-500 placeholder-zinc-500'
              }`}
              style={{ lineHeight: '24px' }}
            />
          </div>
        </div>
      </div>
      
      {error && errorMessage && (
        <div className="mt-1 text-sm text-red-400 px-4 ">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default Input;